import { CommonModule } from "@angular/common";
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Select, SelectModule } from "primeng/select";
import { TooltipModule } from "primeng/tooltip";
import { InputWrapComponent } from "src/app/shared/components/ui/input-wrap.component";
import { TableComponent } from "src/app/shared/components/ui/table/table.component";
import { Order, OrderItem, OrderLine, QCStageOpt } from "src/app/shared/models/order.model";
import { BroshService } from "src/app/shared/services/brosh.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { Store } from "@ngrx/store";
import { qcModeSw, currentOrder, orders, orderNavNextSw, nextOrderOnSamePage as nextOrderOnSamePageSelector } from "src/app/shared/ngrx/ngrx-selectors";
import { MARK_INBOX_NEEDS_RELOAD } from "src/app/shared/ngrx/ngrx-actions";
import { AuthService } from "src/app/shared/services/auth.service";
import { ToasterService } from "src/app/shared/services/toaster.service";

@Component({
  selector: "order-lines",
  templateUrl: "./order-lines.component.html",
  styleUrls: ["./order-lines.component.scss"],
  imports: [
    TranslateModule,
    CommonModule,
    TableComponent,
    FormsModule,
    InputWrapComponent,
    SelectModule,
    TooltipModule,
  ],
  standalone: true,
})
export class OrderLinesComponent implements OnInit {
  @ViewChildren(Select) itemDescrDropdowns: QueryList<Select>;

  @Input() orderId: number;
  @Input() orderLines: OrderLine[];
  @Input() allowEdit: boolean;
  @Input() fromRX: boolean = false;
  @Input() displayClosedOrder: boolean;
  @Output() onSaved = new EventEmitter();

  editMode: boolean;
  displayedOrderLines: OrderLine[] = [];
  items: OrderItem[] = [];
  saving: boolean;

  get checkedRows(): OrderLine[] {
    return this.displayedOrderLines.filter((e) => e.selected);
  }

  get newLines(): OrderLine[] {
    return this.displayedOrderLines.filter((r) => !r.custLineId);
  }

  get qcModeSw(): boolean {
    return this.store.selectSignal(qcModeSw)();
  }

  get currentOrder(): Order {
    return this.store.selectSignal(currentOrder)();
  }


  get allowEditWaybillStage(): boolean {
    return !this.fromRX && this.qcModeSw && this.authService.currentUser.authDetails.getBooleanValue(
      "ORDERS.ALLOW_UPDATE_QC_STATUS"
    ) && this.currentOrder.qcStageOptions.length > 0;
  }

  get orders(): Order[] {
    return this.store.selectSignal(orders)();
  }

  get hasNextOrder(): boolean {
    return this.store.selectSignal(orderNavNextSw)();
  }

  get nextOrderOnSamePage(): boolean {
    return this.store.selectSignal(nextOrderOnSamePageSelector)();
  }

  constructor(
    private translateService: TranslateService,
    private sharedService: SharedService,
    private broshService: BroshService,
    private store: Store,
    private authService: AuthService,
    private toasterService: ToasterService
  ) { }

  ngOnInit(): void {


    this.restoreOrigLines();
    this.getData();



  }

  ngOnDestroy(): void { }

  restoreOrigLines() {
    this.displayedOrderLines =
      JSON.parse(JSON.stringify(this.orderLines)) ?? [];

    this.editMode = this.allowEditWaybillStage;
  }

  getData() {
    this.items.loading = true;
    this.broshService.getItemCatalog(this.orderId).subscribe((data) => {
      this.items = data;
      // a line whose item the order's catalogue lacks keeps the item it came with, so a save does not drop it
      this.displayedOrderLines.forEach(
        (r) => (r.item = this.items.find((i) => i.id == r.itemCode) ?? r.item)
      );
    });
  }

  addLine() {
    let line = new OrderLine(this.displayedOrderLines.length + 1);
    line.newLineSw = true;
    this.displayedOrderLines.push(line);
  }

  toggleEdit(toggle: boolean) {
    this.editMode = toggle;
  }

  onDelete() {
    let validationStr = this.translateService.instant(
      "translate.validation.deleteLinesPrompt"
    );

    this.sharedService.emitPopupchReceivedEvent({
      title: "translate.chat.deleteFilePrompt",
      icon: "exclamation-triangle",
      subTitle: validationStr,
      keep: false,
      approve: () => {
        this.displayedOrderLines = this.displayedOrderLines.filter(
          (r) => !r.selected
        );
      },
    });
  }

  save(stageId?: string) {
    this.saving = true;
    this.displayedOrderLines = this.displayedOrderLines.filter(
      (l) => !l.isEmptyRow
    );
    this.broshService
      .updOrderLines(this.orderId, this.displayedOrderLines, stageId)
      .subscribe(
        (data) => {
          this.editMode = false;
          this.store.dispatch(MARK_INBOX_NEEDS_RELOAD());

          if (stageId) {
            this.toasterService.info("סטטוס חיוב עודכן בהצלחה");
            this.goToNextOrder();
          }

          this.broshService.sendOrderToMMD(this.orderId).subscribe(
            (data) => {
              this.saving = false;
              this.onSaved.emit();


            },
            (err) => {
              this.saving = false;
            }
          );
        },
        (err) => {
          this.saving = false;
        }
      );
  }

  goToNextOrder() {
    if (!this.hasNextOrder) {
      this.sharedService.goToInbox(this.qcModeSw);
    }
    else {
      this.sharedService.goToNextOrder();
    }
  }


  cancel() {
    this.restoreOrigLines();
    this.editMode = false;
  }

  // Only the rows the user can edit must be complete. An existing line shown read only (a closed one, or any outside
  // edit mode) goes back as it came, so it cannot keep the save disabled.
  saveEnabled() {
    return this.displayedOrderLines.every(
      (r) => !this.isRowEditable(r) || (r.item && r.qty > 0 && r.teethValidation) || r.isEmptyRow
    );
  }

  onQtyEdit(row: OrderLine, event: KeyboardEvent) {
    if (event.key == "Enter" || (event.key == "Tab" && !event.shiftKey)) {
      if (
        this.displayedOrderLines.indexOf(row) ==
        this.displayedOrderLines.length - 1 &&
        this.allowEdit
      ) {
        this.addLine();
        setTimeout(() => {
          document.getElementById("inputItemCode_" + (row.seq + 1))?.focus();
        }, 200);
      } else {
        document.getElementById("inputItemCode_" + (row.seq + 1))?.focus();
      }
    }
  }

  onTeethEdit(row: OrderLine, event: KeyboardEvent) {
    let validationResult = this.sharedService.getTeethValidityCode(
      row.teethList
    );
    row.teethValidation = validationResult[0] == 0;
    if (!row.teethValidation) {
      row.teethValidationMsg = this.translateService.instant(
        "translate.validation.teethInput_" + validationResult[0]
      );
      row.qty = null;
    } else {
      row.qty = validationResult[1] == 0 ? null : validationResult[1];
      row.teethValidationMsg = null;
    }
  }

  onTeethKeyPress(row: OrderLine, event: KeyboardEvent) {
    if (event.key == "Enter" || (event.key == "Tab" && !event.shiftKey)) {
      document.getElementById("inputQty_" + row.seq)?.focus();
    }
  }

  onItemCodeEdit(row: OrderLine, event: KeyboardEvent) {
    let item = this.items.find((i) => i.id == row.itemCode);
    row.item = item;
    console.log(event);
    console.log(this.itemDescrDropdowns);
    if (event.key == "Enter") {
      document.getElementById("inputTeeth_" + row.seq)?.focus();
      // this.itemDescrDropdowns
      //   .find((e) => e.el.nativeElement.id == "inputItemDescr_" + row.seq)
      //   ?.applyFocus();
    }
  }

  isRowEditable(row: OrderLine) {
    return (this.editMode && row.editableSw) || !row.custLineId;
  }

  onWaybillStageChange(stage: QCStageOpt) {
    this.save(stage.id);
  }
}
